import React, { useEffect } from "react";
import { Input } from "semantic-ui-react";
import { useSemanticUiInputRef } from "utilities/hookutils";
import { useValidate } from "formvalidation/hooks";
import { saveOrCancel } from "utilities/inputUtils";

interface Props {
  value: string
  onChanged: (newIban: string) => void
  onEnter?: () => void
  onEsc?: () => void
  focusOnMount?: boolean
  validatorKey: string
}

const IbanInput = (props: Props) => {
  const semanticRef = useSemanticUiInputRef();

  useEffect(() => {
    if (props.focusOnMount) {
      semanticRef.ref.current.focus();
    }
  }, []);

  const isValidInput = (input: string): boolean => {
    const sanitized = input.trim().replace(/\s/g, "");

    if (sanitized.length === 0) return true;

    return validIban(sanitized);
  };

  const validate = async (input: string) => {
    return isValidInput(input);
  };

  const formatInput = (input: string, e?) => {
    const sanitized = input.trim()
      .substring(0, serverReference.constants.bankAccount.maxIbanNumberLength)
      .toUpperCase();

    if (isValidInput(sanitized)) {
      e?.preventDefault();
      props.onChanged(sanitized);
      return sanitized;
    }

    return false;
  };

  const onBlur = () => {
    formatInput(props.value);
  };

  const onChange = (_, { value }) => {
    props.onChanged(value);
  };

  const validation = useValidate({
    key: props.validatorKey,
    title: RESX.BankAccount.IbanNumber,
    value: props.value,
    validate: validate,
    ref: semanticRef.ref
  })

  return <Input
    ref={semanticRef.refSetter}
    value={props.value}
    onChange={onChange}
    maxLength={serverReference.constants.bankAccount.maxIbanNumberLength}
    error={validation.error.hasError}
    onBlur={onBlur}
    onKeyDown={e => saveOrCancel(e, { onEnter: props.onEnter, onEsc: props.onEsc, validation, value: props.value })}
    fluid
  />;
};

export default IbanInput;

const validIban = (value: string): boolean => {
  const rearrange =
    value.substring(4, value.length) +
    value.substring(0, 4);
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const alphaMap = {};
  const numbers = [];

  alphabet.forEach((value, index) => {
    alphaMap[value] = index + 10;
  });

  rearrange.split("").forEach((value, index) => {
    numbers[index] = alphaMap[value] || value;
  });

  return modulo(numbers.join("").toString(), 97) === 1;
};

const modulo = (aNumStr: string, aDiv: number) => {
  let tmp = "";
  let i, r;
  for (i = 0; i < aNumStr.length; i++) {
    tmp += aNumStr.charAt(i);
    r = Number(tmp) % aDiv;
    tmp = r.toString();
  }
  return Number(tmp) / 1;
};